import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Quote, PenLine } from "lucide-react";
import { api } from "@/lib/api";

const PLACEHOLDERS = [
  { text: "[Espaço para depoimento de cliente — edite aqui. Conte em uma frase como foi planejar com a Isabela.]", name: "[Nome do cliente]", role: "[Imóvel ou veículo conquistado · Cidade/UF]" },
  { text: "[Espaço para depoimento de cliente — edite aqui. Nada é inventado: os depoimentos reais serão cadastrados por você.]", name: "[Nome do cliente]", role: "[Imóvel ou veículo conquistado · Cidade/UF]" },
  { text: "[Espaço para depoimento de cliente — edite aqui. Dica: peça autorização por escrito antes de publicar.]", name: "[Nome do cliente]", role: "[Imóvel ou veículo conquistado · Cidade/UF]" },
];

export default function Testimonials() {
  const [real, setReal] = useState(null);

  useEffect(() => {
    api
      .get("/testimonials")
      .then(({ data }) => setReal(data || []))
      .catch(() => setReal([]));
  }, []);

  return (
    <section id="depoimentos" className="scroll-mt-24 bg-white py-24 md:py-32" data-testid="testimonials-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between"
        >
          <div className="max-w-2xl">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-600">Depoimentos</p>
            <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl lg:text-4xl">
              Histórias reais, no tempo certo
            </h2>
            <p className="mt-4 text-base leading-relaxed text-slate-600">
              Este espaço está reservado para depoimentos verdadeiros de clientes —
              nada aqui é inventado nem prometido.
            </p>
          </div>
          {real !== null && real.length === 0 && (
            <span className="inline-flex w-fit items-center gap-2 rounded-full bg-amber-50 px-4 py-2 text-xs font-bold text-amber-700">
              <PenLine className="h-3.5 w-3.5" /> Conteúdo editável — placeholders
            </span>
          )}
        </motion.div>

        <div className="mt-12 grid grid-cols-1 gap-5 md:grid-cols-3">
          {(real === null ? [] : real.length > 0 ? real : PLACEHOLDERS).map((t, i) => (
            <motion.figure
              key={i}
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1], delay: i * 0.1 }}
              className={`flex h-full flex-col rounded-3xl p-7 ${
                real && real.length > 0
                  ? "border border-slate-200 bg-white shadow-sm"
                  : "border border-dashed border-slate-300 bg-slate-50/60"
              }`}
              data-testid={real && real.length > 0 ? `testimonial-card-${i + 1}` : `testimonial-placeholder-${i + 1}`}
            >
              <div className="flex items-center justify-between">
                <Quote className="h-6 w-6 text-brand-300" />
                {real && real.length > 0 && t.category && (
                  <span className="rounded-full bg-brand-50 px-3 py-1 text-[11px] font-bold text-brand-600">
                    {t.category === "imovel" ? "Imóvel" : "Veículo"}
                  </span>
                )}
              </div>
              <blockquote className="mt-4 flex-1 text-base leading-relaxed text-slate-600">{t.text}</blockquote>
              <figcaption className="mt-6 flex items-center gap-3 border-t border-slate-200 pt-4">
                {t.photo_url ? (
                  <img src={t.photo_url} alt={`Foto de ${t.name}`} className="h-10 w-10 rounded-full object-cover" loading="lazy" />
                ) : real && real.length > 0 ? (
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-600 font-display text-sm font-bold text-white">
                    {(t.name || "?").trim().charAt(0).toUpperCase()}
                  </span>
                ) : null}
                <div>
                  <p className="font-display text-sm font-bold text-slate-700">{t.name}</p>
                  <p className="mt-0.5 text-xs text-slate-400">
                    {t.role || (t.category ? (t.category === "imovel" ? "Consórcio de imóvel" : "Consórcio de veículo") : "")}
                  </p>
                </div>
              </figcaption>
            </motion.figure>
          ))}
        </div>
      </div>
    </section>
  );
}
